import * as gateway from 'sfx-web-contract';
import { AlkLogger } from '@alk/core';
import { COMMUNICATION_SERVICE, ICommunicationService } from '../../communication';
import { DeepRequired } from '../../../types';
import { finalize, map, share, takeUntil, tap } from 'rxjs/operators';
import { fullNotificationMap, pluckTyped } from '../../../helpers';
import { inject, injectable } from 'inversify';
import { INotificationMessage } from '../../../interfaces';
import { INotificationsResolveAdapterService } from './notifications-resolve-adapter.interface';
import { ISessionLifecycle, SESSION_LIFECYCLE_SERVICE } from '../../session-lifecycle';
import { Observable } from 'rxjs';

@injectable()
export class NotificationsResolveAdapterService implements INotificationsResolveAdapterService {

    @inject(COMMUNICATION_SERVICE) private readonly communicationService: ICommunicationService;
    @inject(SESSION_LIFECYCLE_SERVICE) private readonly sessionLifeCycle: ISessionLifecycle;

    private _resolvedNotifications$: Observable<Array<INotificationMessage>>;

    notificationsObs = (): Observable<Array<INotificationMessage>> =>
        this._resolvedNotifications$ ?? (this._resolvedNotifications$ =
            this.communicationService.dataObservable(gateway.MessageTypes.NotificationResolve).pipe(
                takeUntil(this.sessionLifeCycle.destroyObservable()),
                finalize(() => this.clean()),
                tap(data => this.delete(data.communicationToken)),
                pluckTyped('payload'),
                map(this.decode),
                tap(data => AlkLogger.debug(`[NotificationsResolveAdapterService#notificationsObs] ${ ''
                }received resolved notifications: ${ JSON.stringify(data) }`)),
                share(),
            ));

    resolveNotifications = (notifications: Array<string>): void => {

        AlkLogger.debug(`[NotificationsResolveAdapterService#resolveNotifications] ${ ''
        }notifications to resolve: ${ JSON.stringify(notifications) }`);

        this.notificationsObs();

        const request: DeepRequired<gateway.INotificationResolveRequest> = {
            id: notifications,
        };

        this.communicationService
            .create(
                gateway.NotificationResolveRequest.encode(
                    gateway.NotificationResolveRequest.create(request),
                ).finish(),
                gateway.MessageTypes.NotificationResolve,
            );
    };

    private readonly decode = (data: Uint8Array): Array<INotificationMessage> =>
        gateway.NotificationResolveResponse.decode(data).Notifications?.map(fullNotificationMap) ?? [];

    private clean(): void {
        this._resolvedNotifications$ = null;
    }

    private readonly delete = (token: number): void => this.communicationService.delete(token);
}
